import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Textarea } from '@/components/shadcn/textarea'
import { Checkbox } from '@/components/shadcn/checkbox'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { notify } from '@/lib/notify'
import {
  getGetApiCompaniesWalletTypesQueryKey,
  usePostApiCompaniesWalletTypes,
  usePutApiCompaniesWalletTypesWalletTypeId,
} from '@/api/generated/company-management-user/company-management-user'
import type { DetailedWalletTypeResponse } from '@/api/model'

interface WalletTypeDialogProps {
  open: boolean
  onClose: () => void
  editing?: DetailedWalletTypeResponse | null
}

export function WalletTypeDialog({ open, onClose, editing }: WalletTypeDialogProps) {
  const { t } = useTranslation(['wallet-types', 'common'])
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isFallback, setIsFallback] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // An already-set fallback can't be unchecked here – it's replaced by promoting
  // another type (mirrors the backend behavior).
  const isCurrentFallback = editing?.isFallback ?? false

  const { mutateAsync: create } = usePostApiCompaniesWalletTypes()
  const { mutateAsync: update } = usePutApiCompaniesWalletTypesWalletTypeId()

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '')
      setDescription(editing?.description ?? '')
      setIsFallback(editing?.isFallback ?? false)
    }
  }, [open, editing])

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesWalletTypesQueryKey() })

  const handleSubmit = async () => {
    if (!name.trim()) return
    setIsSubmitting(true)
    try {
      if (editing?.id) {
        await update({
          walletTypeId: editing.id,
          data: { name: name.trim(), description: description.trim() || null, isFallback },
        })
        notify.success(t('toasts.updated'))
      } else {
        await create({
          data: { name: name.trim(), description: description.trim() || null },
        })
        notify.success(t('toasts.created'))
      }
      await invalidate()
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? t('dialog.titleEdit') : t('dialog.titleCreate')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="wallet-type-name">
              {t('dialog.name.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="wallet-type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('dialog.name.placeholder')}
              maxLength={100}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="wallet-type-description">{t('dialog.description.label')}</Label>
            <Textarea
              id="wallet-type-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('dialog.description.placeholder')}
              maxLength={500}
              rows={3}
            />
          </div>

          {editing && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="wallet-type-fallback"
                  checked={isFallback}
                  disabled={isCurrentFallback}
                  onCheckedChange={(checked) => setIsFallback(checked === true)}
                />
                <Label htmlFor="wallet-type-fallback" className="font-normal">
                  {t('dialog.fallback.label')}
                </Label>
              </div>
              {isCurrentFallback && (
                <p className="text-xs text-muted-foreground">{t('dialog.fallback.disabledHint')}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
            {isSubmitting ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
